import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { FileText, Loader2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { supabase } from '@/integrations/supabase/client';
import { buildEmployerCvUploadDescriptor, isExistingStorageObjectError } from '@/lib/forkhire/employerCvUpload';
import { isSupportedPublicCvBrowserFile } from '@/lib/forkhire/publicApplicationCv';

const MAX_SIZE = 10 * 1024 * 1024;

interface ApplicationCvUpdateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  applicationId: string;
  onSuccess?: (result: { score?: number; versionId?: string }) => void;
}

export function ApplicationCvUpdateDialog({
  open,
  onOpenChange,
  jobId,
  applicationId,
  onSuccess,
}: ApplicationCvUpdateDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);

  const close = (nextOpen: boolean) => {
    if (processing) return;
    if (!nextOpen) setFile(null);
    onOpenChange(nextOpen);
  };

  const selectFile = (selected: File | null) => {
    if (!selected) return;
    if (!isSupportedPublicCvBrowserFile(selected.name, selected.type)) {
      toast.error(t('hire.cvScoring.errors.unsupportedFormat', { fileName: selected.name }));
      return;
    }
    if (selected.size > MAX_SIZE) {
      toast.error(t('hire.cvScoring.errors.tooLarge', { fileName: selected.name }));
      return;
    }
    setFile(selected);
  };

  const submit = async () => {
    if (!file) return;
    setProcessing(true);
    let uploadIntentId: string | null = null;
    try {
      const descriptor = await buildEmployerCvUploadDescriptor(file);
      const { data: intent, error: intentError } = await supabase.functions.invoke('cv-upload-intent', {
        body: {
          action: 'create',
          job_id: jobId,
          application_id: applicationId,
          content_sha256: descriptor.contentSha256,
          extension: descriptor.extension,
          mime_type: descriptor.mimeType,
        },
      });
      if (intentError || !intent?.intent_id || !intent?.storage_path) {
        throw new Error(intent?.error || intentError?.message || t('hire.cvUpdate.failed'));
      }
      uploadIntentId = intent.intent_id;

      const { error: uploadError } = await supabase.storage.from('cvs').upload(intent.storage_path, file, {
        contentType: file.type,
        upsert: false,
      });
      if (uploadError && !isExistingStorageObjectError(uploadError)) {
        throw new Error(uploadError.message);
      }

      const { data, error } = await supabase.functions.invoke('update-application-cv', {
        body: {
          application_id: applicationId,
          upload_intent_id: uploadIntentId,
          storage_path: intent.storage_path,
          content_sha256: descriptor.contentSha256,
          mime_type: descriptor.mimeType,
        },
      });
      if (error || data?.error) {
        throw new Error(data?.error || error?.message || t('hire.cvUpdate.failed'));
      }

      if (data?.processing) {
        toast.info(t('hire.cvUpdate.processing'));
        setFile(null);
        onOpenChange(false);
        return;
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['candidate'] }),
        queryClient.invalidateQueries({ queryKey: ['candidates'] }),
        queryClient.invalidateQueries({ queryKey: ['cv-extraction', applicationId] }),
      ]);
      toast.success(t('hire.cvUpdate.success'));
      onSuccess?.({ score: data?.score, versionId: data?.version_id });
      setFile(null);
      onOpenChange(false);
    } catch (error) {
      if (uploadIntentId) {
        await supabase.functions.invoke('cv-upload-intent', {
          body: { action: 'cleanup', intent_id: uploadIntentId },
        }).catch(() => undefined);
      }
      toast.error(error instanceof Error ? error.message : t('hire.cvUpdate.failed'));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('hire.cvUpdate.title')}</DialogTitle>
          <DialogDescription>{t('hire.cvUpdate.description')}</DialogDescription>
        </DialogHeader>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept=".pdf,.docx,.jpg,.jpeg,.png,.webp,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/webp"
          onChange={(event) => selectFile(event.target.files?.[0] ?? null)}
        />
        <button
          type="button"
          className="w-full rounded-xl border border-dashed border-border p-6 text-center hover:bg-muted/30"
          onClick={() => inputRef.current?.click()}
          disabled={processing}
        >
          {file ? <FileText className="mx-auto mb-2 h-7 w-7 text-primary" /> : <Upload className="mx-auto mb-2 h-7 w-7 text-muted-foreground" />}
          <span className="block text-sm font-medium">{file?.name || t('hire.cvUpdate.chooseFile')}</span>
          <span className="mt-1 block text-xs text-muted-foreground">{t('hire.cvUpdate.formats')}</span>
        </button>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => close(false)} disabled={processing}>{t('common.cancel')}</Button>
          <Button type="button" onClick={submit} disabled={!file || processing}>
            {processing && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
            {t('hire.cvUpdate.process')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
